import { useQueryClient } from '@tanstack/react-query';

import {
    useUserController_EndImpersonate,
    useUserController_Logout,
    useUserController_RegisterUser,
    useUserController_ResetPassword,
    useUserController_ResetPasswordRequest,
} from '@/services/hooks';

import type { RegisterUserInput, ResetPasswordInput } from './types';

/** Signs out. Resolves once the server has ended the session. */
export function useLogout(): () => Promise<unknown> {
    const { mutateAsync } = useUserController_Logout();
    return () => mutateAsync();
}

/** Ends an impersonation, clears every cached query and calls `onEnded`. */
export function useEndImpersonation(onEnded: () => void): () => Promise<unknown> {
    const queryClient = useQueryClient();
    const { mutateAsync } = useUserController_EndImpersonate({
        onSuccess: () => {
            queryClient.invalidateQueries();
            onEnded();
        },
    });
    return () => mutateAsync();
}

/** Sets a new password from a reset link. Rejects with the server's error. */
export function useResetPassword(): (input: ResetPasswordInput) => Promise<unknown> {
    const { mutateAsync } = useUserController_ResetPassword();
    return (input) => mutateAsync({ requestBody: input });
}

/** Emails a reset link. Rejects with the server's error. */
export function useRequestPasswordReset(): (email: string) => Promise<unknown> {
    const { mutateAsync } = useUserController_ResetPasswordRequest();
    return (email) => mutateAsync({ requestBody: { email } });
}

/** Completes an invitation. Rejects with the server's error. */
export function useRegisterUser(): (input: RegisterUserInput) => Promise<unknown> {
    const { mutateAsync } = useUserController_RegisterUser();
    return (input) => mutateAsync({ requestBody: input });
}
